import { investor_profile, referral, user } from "@prisma/client";
import { InputPassword } from "@r8er/components/input/password";
import { InputSelect } from "@r8er/components/input/select";
import { InputShortText } from "@r8er/components/input/short-text";
import { ButtonContainer } from "@r8er/components/marketing/button";
import buttonClasses from "@r8er/components/marketing/button/button.module.css";
import { Column, Columns } from "@r8er/components/marketing/columns";
import columnClasses from "@r8er/components/marketing/columns/columns.module.css";
import { Container } from "@r8er/components/marketing/container";
import { Divider } from "@r8er/components/marketing/divider";
import { IconLogoMarkLight } from "@r8er/components/marketing/icons";
import { Layout } from "@r8er/components/marketing/layout";
import layoutClasses from "@r8er/components/marketing/layout/layout.module.css";
import { Newsletter } from "@r8er/components/marketing/newsletter";
import { ReCAPTCHA } from "@r8er/components/recaptcha";
import { api } from "@r8er/lib/api";
import { EMAIL_ADDRESSES } from "@r8er/lib/config/email-addresses";
import { sendEmail } from "@r8er/lib/mail";
import { withMiddleware } from "@r8er/lib/middleware";
import { referral as referralProc } from "@r8er/lib/procedures/referral";
import { register, REGISTRATION_ERRORS } from "@r8er/lib/procedures/register";
import { useStateCallback } from "@r8er/lib/utils";
import { InferGetServerSidePropsType } from "next";
import { log } from "next-axiom";
import Head from "next/head";
import { FormEventHandler, useEffect, useState } from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";

type Referrer = referral & {
  referrer: user & {
    investor_profile: investor_profile;
  };
};

export const getServerSideProps = withMiddleware(
  async ({ req, query: { referral: referralToken }, prisma }) => {
    let referralRecord: Referrer | undefined;

    try {
      if (typeof referralToken === "string") {
        const referrerData = referralProc.parseReferralToken(referralToken);

        referralRecord = await prisma.referral.findFirst({
          where: {
            id: referrerData?.referralId,
            accepted_timestamp: null,
          },
          include: {
            referrer: {
              include: {
                investor_profile: true,
              },
            },
          },
        });
      }
    } catch {}

    let showRegistration =
      !!referralRecord || process.env.HIDE_REGISTRATION !== "true";
    let needV2Captcha = false;

    let {
      accountType,
      email,
      firstname,
      lastname,
      companyName,
      optinTerms,
      optinNewsletter,
    } = await register.parse(req);
    let errors = [];
    let success = false;

    if ("POST" === req.method) {
      if (referralRecord || accountType == "entrepreneur") {
        await register(
          prisma,
          {
            host: req.headers.host,
            referrerId: referralRecord?.referrer?.id,
            sponsorId:
              referralRecord?.sponsored &&
              referralRecord?.referrer?.investor_profile?.id,
            referralRecord: referralRecord,
          },
          await register.parse(req)
        ).catch((e) => {
          if (e === REGISTRATION_ERRORS.TERMS_REQUIRED)
            errors.push("You must accept the Terms & Conditions.");
          else if (e === REGISTRATION_ERRORS.CONFIRMATION_MISMATCH)
            errors.push("Confirmation password does not match.");
          else if (e === REGISTRATION_ERRORS.UNKNOWN_ERROR)
            errors.push(
              "We couldn't sign you up at this time. Please contact us for further help"
            );
          else if (e === REGISTRATION_ERRORS.COMPANY_NAME_REQUIRED)
            errors.push("Company name is required");
          else if (e === REGISTRATION_ERRORS.FIRST_NAME_REQUIRED)
            errors.push("First name is required");
          else if (e === REGISTRATION_ERRORS.LAST_NAME_REQUIRED)
            errors.push("Last name is required");
          else if (e === REGISTRATION_ERRORS.PASSWORD_LENGTH)
            errors.push(
              "Password did not meet the minimum length requirements."
            );
          else if (e === REGISTRATION_ERRORS.CAPTCHA_ERROR)
            errors.push(
              "We were not able to sign you up at this time. Please try again or contact us for further help."
            );
          else if (e === REGISTRATION_ERRORS.V2_CAPTCHA_NEEDED)
            needV2Captcha = true;
          else if (e.duplicate)
            errors.push(
              "Some of the data you entered already exists in the system. If you think this is a mistake please contact us."
            );
          else
            errors.push(
              "We couldn't sign you up at this time. Please try again or contact us for further help."
            );
        });

        if (needV2Captcha)
          errors.push(
            "We couldn't verify that you are a real human. Please complete the CAPTCHA and try again."
          );

        success = errors.length == 0;
      } else {
        const enquiryData = {
          accountType,
          firstName: firstname,
          lastName: lastname,
          companyName,
          email,

          message: `This user has requested an ${accountType} account on R8ER via the sign-up form.`,
          optin_terms: optinTerms,
          optin_newsletter: optinNewsletter,
        };

        await sendEmail(EMAIL_ADDRESSES.CONTACT, enquiryData, "enquiry");
      }

      success = errors.length == 0;
    }
    return {
      props: {
        success,
        showRegistration,
        needV2Captcha,
        referralRecord,
        errors,
        type: referralRecord ? referralRecord.type : accountType,
        email,

        firstname,
        lastname,
        companyName,
      },
    };
  },
  { mode: "unauthenticated" }
);

const Watermark = () => (
  <svg
    style={{
      position: "absolute",
      top: 0,
      left: 0,
      transform: "translate(-75%, -15%)",
      opacity: "50%",
    }}
    xmlns="http://www.w3.org/2000/svg"
    width="649.6"
    height="1051.7"
  >
    <path
      fill="#f0f0f0"
      d="M516 79a270 270 0 00-382 0 270 270 0 00-23 356l49-48c-56-79-49-190 22-260a203 203 0 01286 0c79 79 79 207 0 286L325 556l-74-73-48 47 122 122 191-191a270 270 0 000-382"
    />
    <path
      fill="#f0f0f0"
      d="M555 497l-15-14-48 47 15 15a258 258 0 010 364 258 258 0 01-364 0 258 258 0 010-364l182-182 74 74 48-48-122-122L95 497a325 325 0 000 460 325 325 0 00460 0 325 325 0 000-460"
    />
  </svg>
);

const Register = ({
  success,
  type,
  email,

  firstname,
  lastname,
  companyName,
  referralRecord,
  errors: errorsStr,
  needV2Captcha,
}: Omit<
  InferGetServerSidePropsType<typeof getServerSideProps>,
  "showRegistration"
>) => {
  const [recaptchaToken, setRecaptchaToken] = useStateCallback<string>("");
  const [errors, setErrors] = useState<string[]>(errorsStr);
  const { executeRecaptcha } = useGoogleReCaptcha();

  const SubmitForm: FormEventHandler<HTMLFormElement> = async (e) => {
    log.info(`SubmitForm`);
    e.preventDefault();
    setErrors(errorsStr);
    const passwordStrengthElement =
      e?.currentTarget?.elements?.["password-strength"];
    const passwordFeedbackWarningElement =
      e?.currentTarget?.elements?.["password-feedback-warning"];
    const passwordFeedbackSuggestionsElement =
      e?.currentTarget?.elements?.["password-feedback-suggestions"];
    if (passwordStrengthElement) {
      const passwordStrength = parseInt(passwordStrengthElement.value);
      if (passwordStrength < 2) {
        return setErrors((e) => [
          ...e,
          `Password too weak: ${
            passwordFeedbackWarningElement?.value ||
            passwordFeedbackSuggestionsElement?.value ||
            "Password too short. Please enter at least 4 characters"
          }`,
        ]);
      }
    }
    const gReCaptchaToken = await executeRecaptcha("enquiryFormSubmit");

    await setRecaptchaToken(gReCaptchaToken, (token) => {
      log.info(`Received reCAPTCHA token ${token}`);

      // @ts-ignore
      e.target.submit();
    });
  };

  const [accountType, setAccountType] = useState("entrepreneur");

  useEffect(() => {
    console.log("accountType", accountType);
  }, [accountType]);

  const accountTypeOptions = [
    { value: "entrepreneur", label: "I am an entrepreneur..." },
    { value: "investor", label: "I am an investor..." },
  ];

  return (
    <div>
      <Head>
        <title>Register Interest - R8ER</title>
        <link rel="icon" href="/favicon.png" />
      </Head>

      <Layout>
        <div style={{ position: "relative", overflow: "hidden" }}>
          <Watermark />

          <Container width={1100}>
            <h1
              style={{
                marginTop: "5rem",
                marginBottom: "-4rem",
                color: "var(--brand-secondary)",
                textAlign: "center",
                fontSize: "2.625rem",
                fontWeight: "normal",
              }}
            >
              {accountType === "investor"
                ? "Join R8ER investor waitlist"
                : "Sign up to R8ER"}
            </h1>
            <Divider />
          </Container>

          <Container width={1100}>
            <div>
              {success && (
                <div
                  style={{ marginBottom: "7.5rem" }}
                  className={layoutClasses.marketing__notice}
                >
                  Thank you for registering. A member of the R8ER team will be
                  in touch as soon as possible.
                </div>
              )}

              {!success && (
                <form method="post" onSubmitCapture={SubmitForm}>
                  {referralRecord?.sponsored && (
                    <div>Sponsored registration</div>
                  )}
                  <Columns count={2}>
                    <Column style={{ overflow: "visible" }}>
                      <InputSelect
                        id="accountType"
                        name="accountType"
                        placeholder="Account type"
                        defaultValue={type ? type : "entrepreneur"}
                        onChange={(e) =>
                          setAccountType(e.newValue as unknown as string)
                        }
                        options={
                          referralRecord?.type
                            ? accountTypeOptions.filter(
                                (o) => o.value === referralRecord.type
                              )
                            : accountTypeOptions
                        }
                        theme={{
                          selectedOptionContainer:
                            layoutClasses.marketing__layout_input_selectedOptionsContainer,
                          selectContainer:
                            layoutClasses.marketing__layout_input_selectContainer,
                          selectButton:
                            layoutClasses.marketing__layout_input_selectButton,
                          activeButton:
                            layoutClasses.marketing__layout_input_activeButton,
                          selectOptions:
                            layoutClasses.marketing__layout_input_selectOptions,
                          selectOption:
                            layoutClasses.marketing__layout_input_selectOption,
                        }}
                      />
                      <InputShortText
                        id="firstname"
                        name="firstname"
                        placeholder="First name"
                        inputMode="text"
                        defaultValue={firstname}
                        required
                      />
                      <InputShortText
                        id="lastname"
                        name="lastname"
                        placeholder="Last name"
                        inputMode="text"
                        defaultValue={lastname}
                        required
                      />
                      <InputShortText
                        id="companyName"
                        name="companyName"
                        placeholder="Company name"
                        inputMode="text"
                        defaultValue={companyName}
                        required
                      />
                    </Column>
                    <Column>
                      <InputShortText
                        id="email"
                        name="email"
                        defaultValue={referralRecord?.email ?? email ?? ""}
                        placeholder="Email"
                        inputMode="email"
                      />

                      {(accountType == "entrepreneur" || referralRecord) && (
                        <div>
                          <InputPassword
                            id="password"
                            name="password"
                            placeholder="Password"
                            showStrength
                          />
                          <InputPassword
                            id="password-confirm"
                            name="password-confirm"
                            placeholder="Confirm password"
                          />
                        </div>
                      )}
                    </Column>
                  </Columns>
                  <Columns count={2} style={{ textAlign: "center" }}>
                    <Column>
                      <div className={layoutClasses.checkbox_label}>
                        <input type="checkbox" name="optinTerms" value="1" /> I
                        accept R8ERs&nbsp;
                        <span className="a" style={{ marginRight: "30px" }}>
                          <a target="_blank" href="/terms">
                            Terms &amp; Conditions
                          </a>
                        </span>
                      </div>
                    </Column>
                    <Column>
                      <div className={layoutClasses.checkbox_label}>
                        <input
                          type="checkbox"
                          name="optinNewsletter"
                          value="1"
                        />{" "}
                        I would like to subscribe to the R8ER Newsletter
                      </div>
                    </Column>
                    {/* <Column>
                      {"entrepreneur" === accountType && (
                        <div className={layoutClasses.checkbox_label}>
                          <input
                            type="checkbox"
                            name="seekingSponsorship"
                            value="0"
                          />{" "}
                          Join sponsorship waitlist
                          <br /> (UK companies only)
                        </div>
                      )}
                    </Column> */}
                  </Columns>

                  <ButtonContainer style={{ marginBottom: "10rem" }}>
                    {errors.length > 0 && (
                      <div className={layoutClasses.text__red}>
                        {errors.map((e) => (
                          <div>{e}</div>
                        ))}
                      </div>
                    )}

                    <ReCAPTCHA
                      recaptchaToken={recaptchaToken}
                      type={needV2Captcha ? "v2" : "v3"}
                    />
                    <button
                      disabled={false}
                      className={[
                        buttonClasses.button_blue,
                        buttonClasses.button_wide,
                      ].join(" ")}
                    >
                      Get Started
                    </button>
                  </ButtonContainer>
                </form>
              )}
            </div>
          </Container>

          <Newsletter />
        </div>
      </Layout>
    </div>
  );
};

const SignUp = () => {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formSubmitting, setFormSubmitting] = useState(false);

  const [email, setEmail] = useState("");
  const [error, setError] = useState(false);

  const setAndValidate = (field: string, e) => {
    setEmail(e.target.value);
    setError(!e.target.value.match(/.+@.+\..+/));
  };

  const submitForm = async () => {
    if (error || !email) {
      return;
    }

    setFormSubmitting(true);

    // Send enquiry.
    await api.mutation("quickenquiry.create", {
      enquiry: { email: email },
    });

    setFormSubmitted(true);
  };

  return (
    <div>
      <Head>
        <title>Register Interest - R8ER</title>
        <link rel="icon" href="/favicon.png" />
      </Head>

      <Layout>
        <Columns
          count={2}
          style={{
            minHeight: "950px",
            gridColumnGap: 0,
            borderBottom: "8px solid var(--brand-secondary)",
          }}
        >
          <Column
            classList={[columnClasses.marketing__columns_inner_background]}
          >
            <div className={columnClasses.marketing__columns_inner_center_wide}>
              <Watermark />

              <h2 style={{ color: "var(--brand-secondary)" }}>
                Register Interest
              </h2>

              {!formSubmitted && (
                <div>
                  <form>
                    <input
                      className={`${error ? layoutClasses.input_error : ""}`}
                      type="email"
                      placeholder="Email"
                      onChange={(e) => setAndValidate("email", e)}
                    />
                  </form>
                  <button
                    disabled={formSubmitting}
                    className={`button_blue`}
                    onClick={() => submitForm()}
                  >
                    {!formSubmitting ? "Register" : "Sending..."}
                  </button>
                </div>
              )}

              {formSubmitted && (
                <div className={layoutClasses.marketing__notice}>
                  Thank you for registering interest in R8ER.
                </div>
              )}
            </div>
          </Column>
          <Column
            classList={[columnClasses.marketing__columns_inner_overlay]}
            style={{
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundImage: "url(/signin-cover.png)",
            }}
          >
            <div
              className={columnClasses.marketing__columns_inner_center_wide}
              style={{ maxWidth: "550px" }}
            >
              <h2>Coming Soon</h2>
              <p>
                The world's first, professionally vetted, digital evaluation
                platform for disruptive healthcare that connects technology with
                investment.
              </p>
              <div style={{ marginTop: "4rem" }}>
                <IconLogoMarkLight />
              </div>
            </div>
          </Column>
        </Columns>

        <Newsletter />
      </Layout>
    </div>
  );
};

export default function Page({
  showRegistration,
  ...props
}: InferGetServerSidePropsType<typeof getServerSideProps>) {
  return showRegistration ? <Register {...props} /> : <SignUp />;
}
